// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Local-storage layer for cached trend rows: cache key helpers, a tiny
// DJB2-style hash, the per-flight fingerprint used to build cache keys,
// and the read/write/remove/clear-for-aircraft persistence helpers.
//
// The in-memory trendCache + trendCacheKeyMemo Maps and the higher-level
// trendCacheKey / clearTrendRowsForAircraft orchestrators stay in
// app.js because they need to inspect mutable app state.
//
// Exposed as a factory because trendCacheStoragePrefix and
// trendCacheSchemaVersion live in app.js.

export function createTrendRowStorage({
  trendCacheStoragePrefix,
  trendCacheSchemaVersion,
}) {
  function trendHashString(value) {
    let hash = 5381;
    const text = String(value ?? "");
    for (let index = 0; index < text.length; index += 1) {
      hash = ((hash << 5) + hash) ^ text.charCodeAt(index);
    }
    return (hash >>> 0).toString(36);
  }

  function trendStorageKey(key) {
    return `${trendCacheStoragePrefix}${encodeURIComponent(key)}`;
  }

  function trendFlightFingerprint(entry) {
    return [
      entry.id,
      entry.path,
      entry.parser,
      entry.title,
      entry.monitor,
    ].map((value) => String(value ?? "")).join("~");
  }

  function readStoredTrendRows(key) {
    try {
      const raw = localStorage.getItem(trendStorageKey(key));
      if (!raw) return null;
      const payload = JSON.parse(raw);
      if (payload?.version !== trendCacheSchemaVersion || payload?.key !== key || !Array.isArray(payload?.rows)) return null;
      return payload.rows;
    } catch {
      return null;
    }
  }

  function writeStoredTrendRows(key, rows) {
    try {
      localStorage.setItem(trendStorageKey(key), JSON.stringify({
        version: trendCacheSchemaVersion,
        key,
        cachedAt: new Date().toISOString(),
        rows,
      }));
    } catch {
      // Storage can be unavailable or full; the in-memory cache still avoids repeated work in this session.
    }
  }

  function removeStoredTrendRows(key) {
    try {
      localStorage.removeItem(trendStorageKey(key));
    } catch {}
  }

  function clearStoredTrendRowsForAircraft(aircraft) {
    const token = `|${aircraft}|`;
    try {
      Object.keys(localStorage)
        .filter((key) => key.startsWith(trendCacheStoragePrefix))
        .forEach((storageKey) => {
          const decoded = decodeURIComponent(storageKey.slice(trendCacheStoragePrefix.length));
          if (decoded.includes(token)) localStorage.removeItem(storageKey);
        });
    } catch {}
  }

  return {
    trendHashString,
    trendStorageKey,
    trendFlightFingerprint,
    readStoredTrendRows,
    writeStoredTrendRows,
    removeStoredTrendRows,
    clearStoredTrendRowsForAircraft,
  };
}
